import React, { useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider } from 'styled-components';
import { useCurrentTheme } from '../hooks';
import { useViewport } from './viewport.context';

/**
 * @type {React.Context<RootState>}
 */
const RootStateContext = React.createContext({});

/**
 * @typedef {Object} RootState
 * @property {ISbStories} categoryPostsSbStories
 * @property {(value: ISbStories) => void} setCategoryPostsSbStories
 * @property {string} categoryFullSlug
 * @property {(value: string) => void} setCategoryFullSlug
 * @property {string} categoryFeaturedPostFullSlug
 * @property {(value: string) => void} setCategoryFeaturedPostFullSlug
 * @property {*} post
 * @property {(value: any) => void} setPost
 * @property {any[]} relatedPosts
 * @property {(value: any[]) => void} setRelatedPosts
 * @property {any} shopifyProduct
 * @property {(value: any) => void} setShopifyProduct
 * @property {any[]} productColors
 * @property {(value: any[]) => void} setProductColors
 * @property {'show' | 'hide' | 'showHide'} fixedBottomSetting
 * @property {(value: 'show' | 'hide' | 'showHide') => void} setFixedBottomSetting
 * @property {any[]} productVariantStories
 * @property {(value: any[]) => void} setProductVariantStories
 */

/**
 * @returns {RootState}
 */
export const useRootState = () => {
  /**
   * @type {RootState}
   */
  const context = useContext(RootStateContext);
  if (!context) {
    throw new Error('useRootState must be used within the RootStateProvider');
  }
  return context;
};

const RootStateProvider = ({ children }) => {
  const [categoryPostsSbStories, setCategoryPostsSbStories] = useState(null);
  const [categoryFullSlug, setCategoryFullSlug] = useState(null);
  const [categoryFeaturedPostFullSlug, setCategoryFeaturedPostFullSlug] = useState(null);
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [shopifyProduct, setShopifyProduct] = useState(null);
  const [productColors, setProductColors] = useState([]);
  const [fixedBottomSetting, setFixedBottomSetting] = useState('hide');
  const [productVariantStories, setProductVariantStories] = useState([]);

  const { viewWidth } = useViewport();
  const currentTheme = useCurrentTheme(viewWidth);

  const contextValue = useMemo(
    () => ({
      categoryPostsSbStories,
      setCategoryPostsSbStories,
      categoryFullSlug,
      setCategoryFullSlug,
      categoryFeaturedPostFullSlug,
      setCategoryFeaturedPostFullSlug,
      post,
      setPost,
      relatedPosts,
      setRelatedPosts,
      shopifyProduct,
      setShopifyProduct,
      productColors,
      setProductColors,
      fixedBottomSetting,
      setFixedBottomSetting,
      productVariantStories,
      setProductVariantStories,
    }),
    [
      categoryPostsSbStories,
      categoryFullSlug,
      categoryFeaturedPostFullSlug,
      post,
      relatedPosts,
      shopifyProduct,
      productColors,
      fixedBottomSetting,
      productVariantStories,
    ],
  );

  return (
    <ThemeProvider theme={currentTheme}>
      <RootStateContext.Provider value={contextValue}>{children}</RootStateContext.Provider>
    </ThemeProvider>
  );
};

RootStateProvider.propTypes = {
  children: PropTypes.any,
};

/**
 * Overrides a subset of RootState for a subtree with values already available synchronously
 * (e.g. a page template's own build-time query data), instead of pushing them into the shared
 * global RootStateProvider via an effect — which never runs during Gatsby's static rendering.
 * Merges with the outer context value so unrelated fields (e.g. Layout.jsx's
 * categoryPostsSbStories/fixedBottomSetting) keep falling through to their normal defaults.
 * @param {{ value: Partial<RootState>, children: React.ReactNode }} props
 */
export const RootStateOverride = ({ value, children }) => {
  const outerValue = useRootState();
  const mergedValue = useMemo(() => ({ ...outerValue, ...value }), [outerValue, value]);

  return <RootStateContext.Provider value={mergedValue}>{children}</RootStateContext.Provider>;
};

RootStateOverride.propTypes = {
  value: PropTypes.object.isRequired,
  children: PropTypes.any,
};

export default RootStateProvider;
